import React, { useState } from 'react';
import * as XLSX from 'xlsx';
import { SellTransaction } from '../services/types';
import { fmtTR } from '../utils/format';

interface SellTransactionExcelUploadProps {
  onImport: (transactions: SellTransaction[]) => void;
  onClose: () => void;
}

const SellTransactionExcelUpload: React.FC<SellTransactionExcelUploadProps> = ({ onImport, onClose }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [preview, setPreview] = useState<any[]>([]);
  const [allTransactions, setAllTransactions] = useState<SellTransaction[]>([]);
  const [showPreview, setShowPreview] = useState(false);

  const parseExcelDate = (dateValue: any): string => {
    if (!dateValue) return new Date().toISOString().split('T')[0];

    // String formatları
    if (typeof dateValue === 'string') {
      // D.MM.YYYY veya DD.MM.YYYY
      const dmyMatch = dateValue.match(/^(\d{1,2})\.(\d{1,2})\.(\d{4})$/);
      if (dmyMatch) {
        const [, day, month, year] = dmyMatch;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      // DD/MM/YYYY
      const slashMatch = dateValue.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
      if (slashMatch) {
        const [, day, month, year] = slashMatch;
        return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      }
      // YYYY-MM-DD
      const ymdMatch = dateValue.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      if (ymdMatch) return dateValue;

      const parsedDate = new Date(dateValue);
      if (!isNaN(parsedDate.getTime())) {
        return parsedDate.toISOString().split('T')[0];
      }
    }

    // Excel serial number (ör: 45356)
    if (typeof dateValue === 'number') {
      const excelEpoch = new Date(1900, 0, 1);
      const daysOffset = dateValue - 1;
      const jsDate = new Date(excelEpoch.getTime() + daysOffset * 24 * 60 * 60 * 1000);
      if (dateValue > 59) {
        jsDate.setDate(jsDate.getDate() - 1);
      }
      return jsDate.toISOString().split('T')[0];
    }

    return new Date().toISOString().split('T')[0];
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      // Check file type
      const validTypes = [
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        'text/csv'
      ];
      
      if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(xlsx|xls|csv)$/)) {
        setError('Lütfen geçerli bir Excel dosyası (.xlsx, .xls) veya CSV dosyası yükleyin.');
        return;
      }

      setFile(selectedFile);
      setError('');
      setPreview([]);
      setShowPreview(false);
    }
  };

  const parseExcelFile = async () => {
    if (!file) return;

    setLoading(true);
    setError('');

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data, { type: 'array' });
      const sheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      if (jsonData.length < 2) {
        setError('Excel dosyasında veri bulunamadı. En azından başlık ve bir veri satırı olmalı.');
        return;
      }

      const headers = jsonData[0] as string[];
      const rows = jsonData.slice(1) as any[][];

      // Validate required columns
      const requiredColumns = ['Varlık', 'Tür', 'Miktar', 'Satış Fiyatı', 'Alış Fiyatı', 'Satış Tarihi'];
      const missingColumns = requiredColumns.filter(col => !headers.includes(col));
      
      if (missingColumns.length > 0) {
        setError(`Eksik sütunlar: ${missingColumns.join(', ')}. Lütfen şablon formatını kontrol edin.`);
        return;
      }

      // Parse and validate data
      const transactions: SellTransaction[] = [];
      const errors: string[] = [];
      const allTransactions: SellTransaction[] = []; // Store all transactions for full import

      rows.forEach((row, index) => {
        try {
          const rowIndex = index + 2; // Excel satır numarası (header + 1-indexed)
          
          // Get values by column name
          const getValue = (columnName: string) => {
            const colIndex = headers.indexOf(columnName);
            return row[colIndex];
          };

          const symbol = getValue('Varlık');
          const type = getValue('Tür');
          const quantity = parseFloat(getValue('Miktar'));
          const sellPrice = parseFloat(getValue('Satış Fiyatı'));
          const buyPrice = parseFloat(getValue('Alış Fiyatı'));
          const sellDateStr = getValue('Satış Tarihi');

          // Validation
          if (!symbol || !type || isNaN(quantity) || isNaN(sellPrice) || isNaN(buyPrice) || !sellDateStr) {
            errors.push(`Satır ${rowIndex}: Eksik veya geçersiz veri`);
            return;
          }

          if (quantity <= 0 || sellPrice <= 0 || buyPrice <= 0) {
            errors.push(`Satır ${rowIndex}: Miktar ve fiyatlar pozitif olmalıdır`);
            return;
          }

          // Parse date using parseExcelDate helper
          let sellDate: Date;
          try {
            const rawDateValue = getValue('Satış Tarihi');
            const parsedDateStr = parseExcelDate(rawDateValue);
            sellDate = new Date(parsedDateStr);
            
            if (isNaN(sellDate.getTime())) {
              throw new Error(`Geçersiz tarih formatı: ${rawDateValue}`);
            }
          } catch (error) {
            errors.push(`Satır ${rowIndex}: Geçersiz tarih formatı - ${getValue('Satış Tarihi')}`);
            return;
          }

          // Validate type
          const normalizedType = type.toString().toLowerCase().trim();
          let assetType: 'stock' | 'currency' | 'gold' | 'crypto';

          if (['stock', 'hisse'].includes(normalizedType)) {
            assetType = 'stock';
          } else if (['currency', 'döviz'].includes(normalizedType)) {
            assetType = 'currency';
          } else if (['gold', 'altın'].includes(normalizedType)) {
            assetType = 'gold';
          } else if (['crypto', 'kripto'].includes(normalizedType)) {
            assetType = 'crypto';
          } else {
            errors.push(`Satır ${rowIndex}: Geçersiz varlık türü: ${type}`);
            return;
          }

          // Calculate realized profit
          const realizedProfit = (sellPrice - buyPrice) * quantity;
          const realizedProfitPercent = buyPrice > 0 ? ((sellPrice - buyPrice) / buyPrice) * 100 : 0;
          
          // Default USD rate (can be updated later)
          const usdRate = 32.0; // Varsayılan dolar kuru
          const realizedProfitUSD = realizedProfit / usdRate;
          const realizedProfitPercentUSD = realizedProfitPercent; // Same percentage in USD

          const transaction: SellTransaction = {
            id: `import_${Date.now()}_${index}`,
            symbol: symbol.toString().trim(),
            name: symbol.toString().trim(), // Will be updated with real name if available
            type: assetType,
            quantity,
            sellPrice,
            buyPrice,
            sellDate: sellDate.toISOString().split('T')[0],
            realizedProfit,
            realizedProfitPercent,
            usdRate,
            realizedProfitUSD,
            realizedProfitPercentUSD
          };

          transactions.push(transaction);
          allTransactions.push(transaction);
        } catch (error) {
          errors.push(`Satır ${index + 2}: Veri işleme hatası - ${error}`);
        }
      });

      if (errors.length > 0) {
        setError(`Bazı satırlar işlenemedi:\n${errors.slice(0, 5).join('\n')}${errors.length > 5 ? `\n... ve ${errors.length - 5} satır daha` : ''}`);
        if (transactions.length === 0) return;
      }

      if (transactions.length === 0) {
        setError('İşlenebilir veri bulunamadı.');
        return;
      }

      // Show preview
      setPreview(transactions.slice(0, 5)); // Show first 5 transactions
      setAllTransactions(allTransactions); // Store all transactions for full import
      setShowPreview(true);

    } catch (error) {
      console.error('Excel parsing error:', error);
      setError('Dosya okunurken hata oluştu. Lütfen dosya formatını kontrol edin.');
    } finally {
      setLoading(false);
    }
  };

  const handleImport = () => {
    if (preview.length > 0) {
      onImport(preview);
      onClose();
    }
  };

  const handleImportAll = () => {
    if (allTransactions.length > 0) {
      onImport(allTransactions);
      onClose();
    }
  };

  const downloadTemplate = () => {
    const template = [
      ['Varlık', 'Tür', 'Miktar', 'Satış Fiyatı', 'Alış Fiyatı', 'Satış Tarihi'],
      ['THYAO', 'hisse', 100, 285.50, 275.30, '01.04.2026'],
      ['USD', 'döviz', 500, 32.85, 31.95, '02.04.2026'],
      ['Çeyrek Altın', 'altın', 1, 1950.00, 1850.00, '03.04.2026'],
      ['BTC', 'kripto', 0.01, 3500000, 3200000, '04.04.2026']
    ];

    const ws = XLSX.utils.aoa_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Satış Geçmişi');
    XLSX.writeFile(wb, 'satis_gecmisi_sablon.xlsx');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content sell-excel-upload-modal">
        <div className="modal-header">
          <h3>📊 Satış Geçmişi Toplu Yükleme</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <div className="upload-instructions">
            <h4>📋 Kullanım Talimatları</h4>
            <ul>
              <li>Excel dosyası (.xlsx, .xls) veya CSV dosyası yükleyin</li>
              <li>Dosya şu sütunları içermelidir: Varlık, Tür, Miktar, Satış Fiyatı, Alış Fiyatı, Satış Tarihi</li>
              <li>Tür sütunu için: "hisse", "döviz", "altın", "kripto" kullanın</li>
              <li>Tarih formatı: GG.AA.YYYY (örn: 01.04.2026)</li>
              <li>Fiyatlar TL cinsinden olmalıdır</li>
            </ul>
            
            <button onClick={downloadTemplate} className="template-download-btn">
              📥 Şablon İndir
            </button>
          </div>

          <div className="file-upload-area">
            <input
              type="file"
              id="sell-excel-file"
              accept=".xlsx,.xls,.csv"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <label htmlFor="sell-excel-file" className="file-upload-label">
              <div className="upload-icon">📁</div>
              <div className="upload-text">
                {file ? file.name : 'Excel dosyasını buraya sürükleyin veya tıklayın'}
              </div>
              <div className="upload-hint">
                Desteklenen formatlar: .xlsx, .xls, .csv
              </div>
            </label>
          </div>

          {file && !showPreview && (
            <div className="file-actions">
              <button 
                onClick={parseExcelFile} 
                disabled={loading}
                className="parse-button"
              >
                {loading ? '📊 İşleniyor...' : '📊 Dosyayı Oku'}
              </button>
            </div>
          )}

          {error && (
            <div className="error-message">
              <strong>⚠️ Hata:</strong>
              <pre>{error}</pre>
            </div>
          )}

          {showPreview && preview.length > 0 && (
            <div className="preview-section">
              <h4>📋 Ön İzleme (İlk 5 Kayıt / Toplam {allTransactions.length} Kayıt)</h4>
              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      <th>Varlık</th>
                      <th>Tür</th>
                      <th>Miktar</th>
                      <th>Satış Fiyatı</th>
                      <th>Alış Fiyatı</th>
                      <th>Kar/Zarar</th>
                      <th>Satış Tarihi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.map((transaction, index) => (
                      <tr key={index}>
                        <td>{transaction.symbol}</td>
                        <td>{transaction.type}</td>
                        <td>{transaction.quantity}</td>
                        <td>₺{fmtTR(transaction.sellPrice)}</td>
                        <td>₺{fmtTR(transaction.buyPrice)}</td>
                        <td className={transaction.realizedProfit >= 0 ? 'positive' : 'negative'}>
                          ₺{fmtTR(transaction.realizedProfit)}
                        </td>
                        <td>{new Date(transaction.sellDate).toLocaleDateString('tr-TR', { 
                          day: '2-digit', 
                          month: '2-digit', 
                          year: 'numeric' 
                        })}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              <div className="preview-actions">
                <button onClick={handleImport} className="import-button">
                  ✅ İlk 5 Kaydı İçe Aktar
                </button>
                <button onClick={handleImportAll} className="import-all-button">
                  📊 Tümünü İçe Aktar ({allTransactions.length} Kayıt)
                </button>
                <button onClick={onClose} className="cancel-button">
                  ❌ İptal
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SellTransactionExcelUpload;
